<template>
  <OrderTestDialog 
    title="Edit Patient Price - This Order Only"
    :is-show-dialog="isShowDialog" 
    @close="$emit('close')"
  >
    <Alert type="info">
      <div class="flex items-center p-3">
        <span class="material-icons pr-3 error-icon-size">error</span>
        <p>This will only affect this order, not the default price.</p>
      </div>
    </Alert>

    <div>
      <div class="header mt-4 mb-4">
        <span>{{ itemName }}</span>
      </div>
      <div class="test-detail-clinic">
        <div>
          <div class="flex text-row"><span class="pr-2 text-header">Test type:</span> <span>{{ itemType }}</span></div>
        </div>
        <div>
          <div class="flex text-row"><span class="pr-2 text-header">Wholesale Price:</span> <span>{{ currencySign + this.itemPrice.toFixed(2)  }}</span></div>
        </div>
      </div>
    </div>

    <div class=" mt-2 mb-4">
        <div>Customized Name (optional)</div>
        <el-input
          class="test-name-input"
          v-model="testNameInput"
          type="text"
          :placeholder="'Type customized test name'"
        >
        </el-input>
        <div v-if="isShowTestNameInputError" class="mt-2">
          <Alert :height="48" type="alert">
            <div class="flex items-center restriction-box pt-3">
              <img src="@/assets/orderTest/info_icon_red_15px.svg" class="pl-3 px-2" alt="error outline">
              <div>
                <span class="restriction-text"> If the price update is not applied, name changes are not permitted. </span>
              </div>
            </div>
          </Alert>
        </div>
        <div class="mt-2">
          <Alert :height="80" type="warning" class="mt-2">
            <div class="flex items-center restriction-box pt-2.5 pr-3">
              <img src="@/assets/orderTest/info_icon_yellow_24px.svg" class="pl-3 px-2" alt="error outline">
              <div>
                <span class="restriction-text"> To prevent potential confusion in the materials available to the patient, it is advisable to assign a different name to this item if its current name could lead to any misunderstandings. </span>
              </div>
            </div>
          </Alert>
        </div>
    </div>

    <div class="flex justify-between mb-4 mt-2">
      <div class="w-full">
        <div>Revenue</div>
        <el-input
          class="value-input"
          :class="isMarkupIneligible ? 'error' : ''"
          v-model="markupPrice"
          @input="onValueInput"
          placeholder="Type your revenue..."
        >
          <template v-slot:prefix>
            <div>{{ "+" + currencySign }}</div>
          </template>
        </el-input>
        <div v-if="isMarkupIneligible" class="mt-2">
          <Alert :height="60" type="alert">
            <div class="flex items-center restriction-box pt-3">
              <img src="@/assets/orderTest/info_icon_red_15px.svg" class="pl-3 px-2" alt="error outline">
              <div>
                <span class="restriction-text"> The retail price must be set between 100% and 150% of the wholesale price. </span>
              </div>
            </div>
          </Alert>
        </div>
      </div>
    </div>

    <div class="profit-section-container mb-4">
      <div class="new-price-container flex justify-between pb-3">
        <div>Total Patient Price</div>
        <div class="new-markup-price">{{ currencySign + this.chargeToPatient.toFixed(2) }}</div>
      </div>
      <div v-if="benefit > 0" class="line mb-3"></div>
      <div 
        v-if="benefit > 0"
        class="benefit-container flex justify-between"
      >
        <div>Your Revenue</div>
        <div class="new-markup-price">{{  currencySign + this.benefit.toFixed(2) }}</div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <LoginButton class="mr-5" @click="$emit('close')" type="cancel">Cancel</LoginButton>
        <LoginButton @click="updateMarkupPrice" type="primary">Confirm</LoginButton>
      </div>
    </template>
  </OrderTestDialog>
</template>

<script>
import OrderTestDialog from './OrderTestDialog.vue';
import Alert from "./Alert.vue";
import LoginButton from '@/views/login/components/LoginButton.vue';
import { mapState } from 'vuex';
export default {
  emits: ["close", "saveMarkup"],
  components: {
    OrderTestDialog,
    Alert,
    LoginButton,
    // Select,
  },
  props: {
    isShowDialog: {
      type: Boolean,
      default: false
    },
    itemName: {
      type: String,
      default: ""
    },
    itemCategory: {
      type: String,
      default: ""
    },
    itemPrice: {
      type: Number,
      default: 0
    },
    defaultMarkupPrice: {
      type: Number,
      default: 0
    },
    customizedTestName: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      markupPrice: this.defaultMarkupPrice,
      testNameInput: this.customizedTestName,
      isShowTestNameInputError: false,
      markUpOptions: [{
        label: "Update by Revenue",
        value: "fixed_profit",
        prefix: "+" + this.currencySign,
      }],
      defaultOption: "fixed_profit",
    }
  },
  computed:{
    ...mapState(["currency"]),
    currencySign() {
      return this.currency ? this.currency.symbol : "$";
    },
    dialogContents() {
      return [{
        name: "Test Type",
        value: this.itemType
      }, {
        name: "Wholesale Price",
        value: this.currencySign + this.itemPrice.toFixed(2)
      }, {
        name: "Revenue",
        value: null
      }, {
        name: "Retail Price (Total Charge to Patient)",
        value: this.currencySign + this.chargeToPatient.toFixed(2)
      }, {
        name: "Your Revenue",
        value: this.currencySign + this.benefit.toFixed(2)
      }]
    },
    itemType() {
      switch (this.itemCategory) {
        case "va":
          return "Vibrant America Test";
        case "vw":
          return "Vibrant Wellness Test";
        case "discountPanel":
        case "xChooseY":
          return "Discount Panel";
        case "customPanel":
          return "Special Price Custom Panel";
        case "shortcut":
          return "Shortcut Panel";
        case "seasonalPromo":
          return "Promotion";
        default:
          return this.itemCategory;
      }
    },
    chargeToPatient() {
      return this.itemPrice + Number(this.markupPrice);
    },
    benefit() {
      return Number(this.markupPrice);
    },

    isMarkupIneligible() { // markup can not be high
      return Number(this.markupPrice) > this.itemPrice * 0.5 || Number(this.markupPrice) < 0;
    },
    formattedTestNameInput() {
      return this.testNameInput.trim();
    },
  },
  methods: {
    updateMarkupPrice() {
      if ((!this.markupPrice || Number(this.markupPrice) == 0) && this.testNameInput) {
        this.isShowTestNameInputError = true;
        return;
      }
      this.isShowTestNameInputError = false;

      if (this.isMarkupIneligible) {
        return;
      }
      // this.$emit("saveMarkup", Number(this.markupPrice));
      this.$emit("saveMarkup", { markup: Number(this.markupPrice), customizedTestName: this.formattedTestNameInput });
      this.$emit("close");
    },

    onValueInput(event) {
      const cleanedInput = event.replace(/[^\d.]/g, '');
      this.markupPrice = cleanedInput;
    },
  }
}
</script>

<style scoped>
.item-name {
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.error-icon-size {
  font-size: 18px;
}

.header {
  color: #000;
  font-family: Roboto;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 120% */
  letter-spacing: 0.15px;
}

.test-detail-clinic {
  width: 469px;
  padding: 19px 13px;

  border-radius: 4px;
  background: #F9FBFD;
}

.text-row {
  color: #000;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.text-header {
  font-weight: 600;  
}

.profit-section-container {
  height: 62px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.benefit-container {
  color: #000;
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.458px;
}

.line {
  border-top: 1px solid #E6E6E6;
}

.test-name-input {
  box-sizing: border-box;
  width: 100%;
  height: 44px;

  border-radius: 4px;
  border: 1px solid #B5B5B5;
  background: #FFF;
}

.value-input {
  box-sizing: border-box;
  width: 100%;
  height: 44px;

  border-radius: 4px;
  border: 1px solid #B5B5B5;
  background: #FFF;
}

.error-message {
  color: #DA1E28;
}

.restriction-text {
  word-break: break-word;
}
</style>